import { useEffect, useRef, useState } from 'react';

const STEPS = [
  {
    num: '1',
    time: '30s',
    img: '/images/step-1.webp',
    name: 'Install from Shopify',
    desc: 'One-click install from the Shopify App Store — we auto-detect your store, fulfillment platform, and connected apps.',
  },
  {
    num: '2',
    time: '2m',
    img: '/images/step-2.webp',
    name: 'Set your edit window',
    desc: 'Set a default edit window — we suggest 60 minutes — or add per-product rules for pre-orders and same-day cutoffs.',
  },
  {
    num: '3',
    time: '90s',
    img: '/images/step-3.webp',
    name: 'Connect your stack',
    desc: 'Tested with the top 50 Shopify apps like Klaviyo, Gorgias, and ShipStation. Edits write natively into your Shopify Orders.',
  },
  {
    num: '4',
    time: '1m',
    img: '/images/step-4.webp',
    name: 'Go live',
    desc: 'Customers get the edit link in their confirmation email. First edits land within the hour as your ticket queue drains.',
  },
];

export default function Setup() {
  const [active, setActive] = useState(0);
  const [swapping, setSwapping] = useState(false);
  const stepRefs = useRef([]);
  const prevActive = useRef(0);

  // scroll: auto-activate the step crossing the middle of the viewport
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const idx = stepRefs.current.indexOf(en.target);
          if (idx !== -1) setActive(idx);
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    stepRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  // brief fade when the visual changes
  useEffect(() => {
    if (prevActive.current === active) return;
    prevActive.current = active;
    setSwapping(true);
    const t = setTimeout(() => setSwapping(false), 320);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="setup">
      <div className="setup__container">
        <div className="setup__visual">
          <img
            src={STEPS[active].img}
            alt="Set up Account Editor in 5 minutes"
            className={`setup__visual-img${swapping ? ' is-swapping' : ''}`}
          />
        </div>

        <div className="setup__content">
          <div className="setup__head">
            <span className="badge-pill"><i className="ph ph-lightning" aria-hidden="true" /> Live in five minutes</span>
            <h2 className="section-title">Set up Account Editor in 4 steps.</h2>
            <p className="setup__sub">From install to live — no developer, no theme code, no dependencies.</p>
          </div>

          <div className="setup__steps">
            {STEPS.map((s, i) => (
              <div
                key={s.num}
                ref={(el) => (stepRefs.current[i] = el)}
                className={`setup__step${i === active ? ' is-active' : ''}`}
                onClick={() => setActive(i)}
              >
                <span className="setup__step-top">
                  <span className="setup__step-num">{s.num}</span>
                  <span className="setup__step-time">{s.time}</span>
                </span>
                <h3 className="setup__step-name">{s.name}</h3>
                <p className="setup__step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
